import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { IoMdPlay } from "react-icons/io";
import { SocialMediaOption } from "../types";
import { HelpModal } from "../../common/compoents/HelpModal";
import { FormInputs } from "./FormInputs";
import { BlogTitles } from "./BlogTitles";
import { BlogPostDisplay } from "./BlogPostDisplay";
import { useBlogStore } from "../store/blogStore";
import "./BlogPostForm.css";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";

const BlogPostForm: React.FC = () => {
  const [topic, setTopic] = useState<string>("");
  const [targetedKeyword, setTargetedKeyword] = useState<string>("");
  const [language, setLanguage] = useState<SocialMediaOption | null>({
    value: "english",
    label: "English",
  });
  const [creativity, setCreativity] = useState<SocialMediaOption | null>({
    value: "regular",
    label: "Regular",
  });
  const [loadingTitles, setLoadingTitles] = useState<boolean>(false);
  const [loadingPost, setLoadingPost] = useState<boolean>(false);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [blogTitles, setBlogTitles] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [blogPost, setBlogPost] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const MIN_WORDS = 3;

  const { getTitles, getPost } = useBlogStore();

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [errorMessage, blogTitles, blogPost]);

  const countWords = (text: string) => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  const handleGenerateTitles = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!topic) {
      setErrorMessage("Please enter a topic.");
      return;
    }
    const wordCount = countWords(topic);
    if (wordCount < MIN_WORDS) {
      setErrorMessage(
        `Please enter at least ${MIN_WORDS} words in the topic. Current word count: ${wordCount}`
      );
      return;
    }
    if (!creativity?.value) {
      setErrorMessage("Please select a creativity level.");
      return;
    }

    setLoadingTitles(true);
    setErrorMessage(null);
    try {
      const titles = await getTitles(
        topic,
        language?.value || "english",
        creativity.value
      );
      setBlogTitles(titles);
      setSelectedTitle(null);
      setBlogPost(null);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (error) {
      setErrorMessage("An error occurred while generating titles.");
    } finally {
      setLoadingTitles(false);
    }
  };

  const handleGeneratePost = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!selectedTitle) {
      setErrorMessage("Please select a title.");
      return;
    }
    setLoadingPost(true);
    try {
      const post = await getPost(
        selectedTitle,
        targetedKeyword || undefined,
        language?.value || "english",
        creativity?.value || "regular"
      );
      setBlogPost(post);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (error) {
      setErrorMessage("An error occurred while generating the post.");
    } finally {
      setLoadingPost(false);
    }
  };

  const handleExport = () => {
    if (!blogPost) return;
    const exportText = `${blogPost.title}\n\n${blogPost.content}\n\nWord Count: ${blogPost.wordCount}`;
    const blob = new Blob([exportText], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${blogPost.title.toLowerCase().replace(/\s+/g, "_")}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const helpSteps = [
    "Enter a topic for your blog post in the provided text area.",
    "Optionally, add a targeted keyword to optimize your post for SEO.",
    "Select your desired language and creativity level from the dropdowns.",
    "Click 'Generate Titles' to create blog titles.",
    "Select a title from the list, then click 'Generate Post' to generate the full content.",
    "Review the generated post and export it if needed.",
  ];

  return (
    <Container
      className={`${
        !isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
      } rounded`}
      style={{
        width: "100%", 
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: "0px",
      }}
    >
      <Row className="gx-0" style={{ padding: "0px" }}>
        <Col xs={12}>
          <HelpModal
            helpSteps={helpSteps}
            show={showVideoModal}
            close={() => setShowVideoModal(false)}
            videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
          />
          {!isInIframe && (
            <ToolsHeader
              title="Write quality blog posts with AI"
              description="Create high-quality blog posts easily by harnessing the power of Artificial Intelligence."
            />
          )}
          <FormInputs
            topic={topic}
            targetedKeyword={targetedKeyword}
            language={language}
            creativity={creativity}
            loadingTitles={loadingTitles}
            loadingPost={loadingPost}
            errorMessage={errorMessage}
            hasBlogPost={!!blogPost}
            onTopicChange={(e) => setTopic(e.target.value)}
            onKeywordChange={(e) => setTargetedKeyword(e.target.value)}
            onLanguageChange={setLanguage}
            onCreativityChange={setCreativity}
            onGenerateTitles={handleGenerateTitles}
            onExport={handleExport}
          />
          {blogTitles.length > 0 && !blogPost && (
            <BlogTitles
              titles={blogTitles}
              selectedTitle={selectedTitle}
              loading={loadingPost}
              onTitleSelect={setSelectedTitle}
              onGeneratePost={handleGeneratePost}
            />
          )}
          {blogPost && (
            <BlogPostDisplay
              title={blogPost.title}
              content={blogPost.content}
              wordCount={blogPost.wordCount}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default BlogPostForm;